import React from 'react';

import { countryEnumBase, mapDefined, noop } from '@nfctron/nexus-core';
import { UiButton, useContextRequired, useIsMounted } from '@nfctron/nexus-react';

import { Accordion, Dialog, Modal, Switch, Table } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { useSessionStore } from '@root/features/authentication/stores/sessionStore';

import { APP_I18N_NAMESPACE, IS_LOCAL } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { useTracking } from '@root/lib/modules/tracking/TrackingProvider';

import { useCookieConsent } from './cookies.module';
import { type CookieConsentHookParams, DEFAULT_CONSENT } from './cookies.types';

/**
 * Cookie consent context
 */
export const CookieConsentContext = React.createContext<CookieConsentContextProps | undefined>(undefined);
type CookieConsentContextProps = { cookieConsent: ReturnType<typeof useCookieConsent>; openCookiesSettingsModal: () => void };

const getGtmConsentParams = (consent: boolean) => {
	if (consent) {
		return {
			ad_storage: 'granted',
			ad_user_data: 'granted',
			ad_personalization: 'granted',
			analytics_storage: 'granted',
			functionality_storage: 'granted',
			personalization_storage: 'granted',
			security_storage: 'granted',
		};
	}
	return {
		ad_storage: 'denied',
		ad_user_data: 'denied',
		ad_personalization: 'denied',
		analytics_storage: 'denied',
		functionality_storage: 'denied',
		personalization_storage: 'denied',
		security_storage: 'denied',
	};
};

/**
 * Cookie consent provider
 * @constructor
 */
export const CookieConsentProvider: React.FunctionComponent<React.PropsWithChildren<CookieConsentProviderProps>> = ({
	useCookieConsentHooksParams,
	children,
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, CookieConsentProvider.displayName);
	const { t: commonT } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const cookieConsentCtx = useCookieConsent(useCookieConsentHooksParams);
	const [isCookiesDialogOpen, setIsCookiesDialogOpen] = React.useState(false);
	const [isCookiesSettingsModalOpen, setIsCookiesSettingsModalOpen] = React.useState(false);
	const canDebug = IS_LOCAL;
	const isScreenMd = useMediaQuery('(min-width: 768px)');
	const { trackingModule, analyticsModule } = useTracking();
	const isMounted = useIsMounted();
	const account = useSessionStore((state) => state.account?.account);

	/** update tracking consent settings */
	const updateTrackingConsentAll = (consent: boolean) => {
		trackingModule.gtagJsAdapter.gtag?.(
			'consent',
			'update',
			/** @ts-expect-error Not typed, ref: https://developers.google.com/tag-platform/gtagjs/reference#consent or https://support.google.com/tagmanager/answer/10718549?hl=en */
			getGtmConsentParams(consent),
		);
		trackingModule.fbPixelAdapter.fbq?.('consent', consent ? 'grant' : 'revoke');
		trackingModule.sklikAdapter.updateConsent(consent);
		trackingModule.tiktokPixelAdapter.updateConsent(consent);
		analyticsModule.msClarityAdapter.clarity?.('consent', consent);
	};

	/** synchronize account details with some tracking providers */
	React.useEffect(() => {
		if (account) {
			trackingModule.sklikAdapter.updateIdentities({
				eid: account.email,
				tid: account.phone,
				aid: mapDefined(account.billingInfo, (info) => ({
					a1: mapDefined(info.countryId, (countryId) => countryEnumBase.get(countryId)?.getLabel(commonT)),
					a2: info.city,
					a3: info.address?.split(' ')[0],
					a4: info.address?.split(' ')[1],
					a5: info.postalCode,
				})),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [account, trackingModule.sklikAdapter.updateIdentities]);

	/** initial tracking consent settings */
	React.useEffect(() => {
		if (isMounted) {
			/** set initial tracking consent */
			console.debug(
				'[CookieConsentProvider] Initial tracking consent settings set to %s',
				cookieConsentCtx.consent.marketing ? 'granted' : 'revoked',
			);
			trackingModule.gtagJsAdapter.gtag?.(
				'consent',
				'default',
				/** @ts-expect-error Not typed well, see https://developers.google.com/tag-platform/gtagjs/reference#consent or https://support.google.com/tagmanager/answer/10718549?hl=en */
				getGtmConsentParams(cookieConsentCtx.consent.marketing || cookieConsentCtx.consent.statistics || false),
			);
			trackingModule.googleTagManagerAdapter.dataLayer?.push({
				google_tag_params: { consent_info: getGtmConsentParams(cookieConsentCtx.consent.marketing || cookieConsentCtx.consent.statistics || false) },
			});
			trackingModule.sklikAdapter.updateConsent(cookieConsentCtx.consent.marketing || cookieConsentCtx.consent.statistics || false);
			trackingModule.fbPixelAdapter.fbq?.('consent', cookieConsentCtx.consent.marketing ? 'grant' : 'revoke');
			trackingModule.tiktokPixelAdapter.updateConsent(cookieConsentCtx.consent.marketing || false);
			analyticsModule.msClarityAdapter.clarity?.('consent', cookieConsentCtx.consent.marketing);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isMounted,
		trackingModule.gtagJsAdapter.gtag,
		trackingModule.googleTagManagerAdapter.dataLayer,
		trackingModule.fbPixelAdapter.fbq,
		trackingModule.sklikAdapter.updateConsent,
		trackingModule.tiktokPixelAdapter.ttq,
		analyticsModule.msClarityAdapter.clarity,
	]);

	/** sync modal state with cookie consent state */
	React.useEffect(() => {
		const shouldBeOpen = !cookieConsentCtx.isConsentSet;
		setIsCookiesDialogOpen(shouldBeOpen);
	}, [cookieConsentCtx.isConsentSet]);

	/** open cookies modal upon load with a slight delay */
	React.useEffect(() => {
		if (isMounted && !cookieConsentCtx.isConsentSet) {
			const timer = setTimeout(() => setIsCookiesDialogOpen(true), 2048);
			return () => clearTimeout(timer);
		}
	}, [isMounted, cookieConsentCtx.isConsentSet]);

	/** custom screen-sized dialog props */
	const cookiesDialogProps = React.useMemo(() => {
		if (isScreenMd) {
			return {
				shadow: '0 0.625em 1.875em rgba(2,2,3,0.23)',
				size: 910,
				position: { bottom: '1rem', right: 'calc(50% - 400px)' },
				styles: { root: { padding: '1rem !important' } },
			};
		}

		return {
			size: 'full',
			shadow: '0 0.625em 1.875em rgba(2,2,3,0.46)',
			position: { bottom: '0.5rem', left: '0.5rem', right: '0.5rem' },
			styles: { root: { padding: '1rem !important' } },
		};
	}, [isScreenMd]);

	const handleAcceptAll = () => {
		setIsCookiesDialogOpen(false);
		cookieConsentCtx.acceptAllCookies();
		updateTrackingConsentAll(true);
	};

	const handleDeclineAll = () => {
		setIsCookiesDialogOpen(false);
		cookieConsentCtx.declineAllCookies();
		updateTrackingConsentAll(false);
	};

	const cookieConsentContextValue = React.useMemo(
		() => ({
			cookieConsent: cookieConsentCtx,
			openCookiesSettingsModal: () => setIsCookiesSettingsModalOpen(true),
		}),
		[cookieConsentCtx],
	);

	/** render nothing (but children) if not mounted */
	if (!isMounted) {
		return <CookieConsentContext value={cookieConsentContextValue}>{children}</CookieConsentContext>;
	}

	return (
		<CookieConsentContext value={cookieConsentContextValue}>
			{/* main cookies dialog */}
			<Dialog
				opened={isCookiesDialogOpen}
				radius="xl"
				transitionProps={{ transition: 'pop', duration: 250 }}
				className="max-w-none"
				onClose={noop}
				role="alert"
				aria-live="polite"
				{...cookiesDialogProps}
			>
				{/* title */}
				<span className="mb-5 block text-sm font-semibold text-gray-700">{t('cookiesDialog.title')}</span>
				{/* content */}
				<p className="text-sm text-gray-700">
					<span>{t('cookiesDialog.text')}</span>&nbsp;
					<span>
						{/* settings button */}
						<UiButton
							size="unstyled"
							variant="unstyled"
							className="inline-block text-base font-medium! text-gray-700 underline"
							onClick={() => setIsCookiesSettingsModalOpen(true)}
							aria-label={t('cookiesDialog.settingsButton.label')}
						>
							{t('cookiesDialog.settingsButton.label')}
						</UiButton>
					</span>
				</p>
				{/* actions */}
				<div className="mt-3 flex flex-col items-center gap-2 text-sm md:flex-row">
					{/* accept all button */}
					<UiButton size="sm" fullWidth variant="primary" onClick={handleAcceptAll} aria-label={t('acceptAllCookiesButton.label')}>
						{t('acceptAllCookiesButton.label')}
					</UiButton>
					{/* deny all button */}
					<UiButton size="sm" fullWidth variant="subtle" color="lucky-point" onClick={handleDeclineAll} aria-label={t('denyAllCookiesButton.label')}>
						{t('denyAllCookiesButton.label')}
					</UiButton>
				</div>
			</Dialog>

			{/* cookies settings modal */}
			<Modal
				opened={isCookiesSettingsModalOpen}
				onClose={() => setIsCookiesSettingsModalOpen(false)}
				centered
				size="xl"
				title={<span className="block text-base font-semibold text-gray-700">{t('cookiesSettingsModal.title')}</span>}
			>
				{/* text */}
				<p className="text-base text-gray-700">{t('cookiesSettingsModal.text')}</p>

				{/* dev debug button */}
				{canDebug && (
					<UiButton fullWidth className="mt-3 md:w-auto" color="red" variant="subtle" onClick={() => cookieConsentCtx.acceptCookies(DEFAULT_CONSENT)}>
						(local-debug) reset cookies
					</UiButton>
				)}

				{/* content */}
				<div className="mt-4">
					<Accordion chevronPosition="left" variant="separated" radius="md" styles={{ item: { marginTop: '0.5rem !important' } }}>
						{/* necessary */}
						<Accordion.Item value="necessary">
							<div className="flex items-center gap-2 pr-4">
								<Accordion.Control>
									<span className="text-base">{t('cookiesSettingsModal.necessary.title')}</span>
								</Accordion.Control>
								<Switch size="md" checked={cookieConsentCtx.consent.necessary} disabled />
							</div>
							<Accordion.Panel>
								<p className="text-base">{t('cookiesSettingsModal.necessary.text')}</p>
							</Accordion.Panel>
						</Accordion.Item>

						{/* analytics */}
						<Accordion.Item value="statistics">
							<div className="flex items-center gap-2 pr-4">
								<Accordion.Control>
									<span className="text-base">{t('cookiesSettingsModal.statistics.title')}</span>
								</Accordion.Control>
								<Switch
									size="md"
									checked={cookieConsentCtx.consent.statistics}
									onChange={() => {
										cookieConsentCtx.acceptCookies({ ...cookieConsentCtx.consent, statistics: !cookieConsentCtx.consent.statistics });
										/** update tracking consent settings */
										updateTrackingConsentAll(!cookieConsentCtx.consent.statistics);
									}}
								/>
							</div>
							<Accordion.Panel>
								{/* description */}
								<p className="text-base">{t('cookiesSettingsModal.statistics.text')}</p>
								{/* table */}
								<Table className="mt-3 text-base" horizontalSpacing="lg">
									<thead>
										<tr>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.nameCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.providerCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.expirationCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '150px' }}>
												{t('cookiesSettingsModal.descriptionCol.label')}
											</th>
										</tr>
									</thead>
									<tbody className="text-sm text-gray-500">
										<tr className="border-t border-b border-gray-200">
											<td className="px-1.5">_ga, _gat, _gid</td>
											<td className="px-1.5">Google Analytics</td>
											<td className="px-1.5">{t('cookiesSettingsModal.ga.expirationCol.value')}</td>
											<td className="px-1.5">{t('cookiesSettingsModal.ga.descriptionCol.value')}</td>
										</tr>
										<tr>
											<td className="px-1.5">_clck, _clsk, CLID</td>
											<td className="px-1.5">Microsoft Clarity</td>
											<td className="px-1.5">-</td>
											<td className="px-1.5">-</td>
										</tr>
									</tbody>
								</Table>
							</Accordion.Panel>
						</Accordion.Item>

						{/* marketing */}
						<Accordion.Item value="marketing">
							<div className="flex items-center gap-2 pr-4">
								<Accordion.Control>
									<span className="text-base">{t('cookiesSettingsModal.marketing.title')}</span>
								</Accordion.Control>
								<Switch
									size="md"
									checked={cookieConsentCtx.consent.marketing}
									onChange={() => {
										cookieConsentCtx.acceptCookies({ ...cookieConsentCtx.consent, marketing: !cookieConsentCtx.consent.marketing });
										/** update tracking consent settings */
										updateTrackingConsentAll(!cookieConsentCtx.consent.marketing);
									}}
								/>
							</div>
							<Accordion.Panel>
								{/* description */}
								<p className="text-base">{t('cookiesSettingsModal.marketing.text')}</p>
								{/* table */}
								<Table className="mt-3 text-base" horizontalSpacing="lg">
									<thead>
										<tr>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.nameCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.providerCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '75px' }}>
												{t('cookiesSettingsModal.expirationCol.label')}
											</th>
											<th className="truncate px-1.5 text-left text-sm" style={{ maxWidth: '150px' }}>
												{t('cookiesSettingsModal.descriptionCol.label')}
											</th>
										</tr>
									</thead>
									<tbody className="text-sm text-gray-500">
										<tr className="border-t border-b border-gray-200">
											<td className="px-1.5">_fbp</td>
											<td className="px-1.5">Facebook</td>
											<td className="px-1.5">{t('cookiesSettingsModal.fbp.expirationCol.value')}</td>
											<td className="px-1.5">{t('cookiesSettingsModal.fbp.descriptionCol.value')}</td>
										</tr>
										<tr>
											<td className="px-1.5">_ttp</td>
											<td className="px-1.5">TikTok</td>
											<td className="px-1.5">{t('cookiesSettingsModal.ttq.expirationCol.value')}</td>
											<td className="px-1.5">{t('cookiesSettingsModal.ttq.descriptionCol.value')}</td>
										</tr>
									</tbody>
								</Table>
							</Accordion.Panel>
						</Accordion.Item>
					</Accordion>
				</div>

				{/* actions */}
				<div className="mt-4 flex flex-col items-center gap-2 overflow-hidden md:flex-row">
					{/* accept all button */}
					<UiButton
						className="w-full! md:w-auto"
						variant="primary"
						onClick={() => {
							cookieConsentCtx.acceptAllCookies();
							setIsCookiesSettingsModalOpen(false);
							/** update tracking consent settings */
							updateTrackingConsentAll(true);
						}}
						aria-label={t('cookiesSettingsModal.acceptAllButton.label')}
					>
						{t('cookiesSettingsModal.acceptAllButton.label')}
					</UiButton>
					{/* decline all button */}
					<UiButton
						className="w-full! md:w-auto"
						color="lucky-point"
						variant="subtle"
						onClick={() => {
							cookieConsentCtx.declineAllCookies();
							setIsCookiesSettingsModalOpen(false);
							/** update tracking consent settings */
							updateTrackingConsentAll(false);
						}}
						aria-label={t('cookiesSettingsModal.declineAllButton.label')}
					>
						{t('cookiesSettingsModal.declineAllButton.label')}
					</UiButton>

					{/* close button */}
					<div>
						<UiButton
							className="hidden! w-full md:block!"
							variant="ghost"
							onClick={() => setIsCookiesSettingsModalOpen(false)}
							aria-label={t('cookiesSettingsModal.closeButton.label')}
						>
							{t('cookiesSettingsModal.closeButton.label')}
						</UiButton>
					</div>
				</div>
			</Modal>

			{/* children */}
			{children}
		</CookieConsentContext>
	);
};
export type CookieConsentProviderProps = { useCookieConsentHooksParams?: CookieConsentHookParams };
CookieConsentProvider.displayName = 'Cookies/CookieConsentProvider';
export const useCookiesConsentContext = () => useContextRequired(CookieConsentContext);
